<script setup>
import { computed, onBeforeUnmount, onMounted } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { parsePhoneNumber } from 'libphonenumber-js';

const props = defineProps({
  currentChat: {
    required: true,
    type: Object,
  },
});

const marketingCloudSdrBaseUrl = useMapGetter(
  'globalConfig/getMarketingCloudSdrBaseUrl'
);
const marketingCloudSdrBaseUrlSinobase = useMapGetter(
  'globalConfig/getMarketingCloudSdrBaseUrlSinobase'
);
const conversationMetadataGetter = useMapGetter(
  'conversationMetadata/getConversationMetadata'
);
const store = useStore();
const { t } = useI18n();

// 根据域名选择正确的 URL
const selectedBaseUrl = computed(() => {
  const currentDomain = window.location.hostname;
  // 如果域名包含 smarket，使用 MARKETING_CLOUD_SDR_BASE_URL
  // 否则使用 MARKETING_CLOUD_SDR_BASE_URL_sinobase
  if (currentDomain.includes('smarket')) {
    return marketingCloudSdrBaseUrl.value;
  }
  return marketingCloudSdrBaseUrlSinobase.value;
});

// 获取当前会话的 metadata（包含渠道信息）
const currentConversationMetaData = computed(() =>
  conversationMetadataGetter.value(props.currentChat.id)
);

// 获取渠道信息
const channelInfo = computed(() => {
  const customAttributes =
    currentConversationMetaData.value?.custom_attributes || {};
  return customAttributes.channel_info || {};
});

const iframeSrc = computed(() => {
  const baseUrl = selectedBaseUrl.value;
  const contactId = props.currentChat.meta?.sender?.id;
  if (!baseUrl) return '';

  const url = `${baseUrl}/#/big/editEnterpriseSidebar`;
  const params = new URLSearchParams();

  if (contactId) {
    params.append('chatId', contactId);
  }

  // 添加渠道信息参数（JSON 序列化）
  if (Object.keys(channelInfo.value).length > 0) {
    params.append('channelInfo', JSON.stringify(channelInfo.value));
  }

  const queryString = params.toString();
  return queryString ? `${url}?${queryString}` : url;
});

const parseMessageData = data => {
  if (typeof data !== 'string') {
    return data;
  }

  try {
    return JSON.parse(data);
  } catch {
    return data;
  }
};

const isBlank = value => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  return false;
};

const toNormalizedText = value => {
  if (isBlank(value)) return '';
  return String(value).trim();
};

const toE164PhoneNumber = value => {
  const raw = toNormalizedText(value);
  if (!raw) return '';

  const normalizedInput = raw.replace(/\s+/g, '');

  try {
    if (normalizedInput.startsWith('+')) {
      const phone = parsePhoneNumber(normalizedInput);
      return phone?.isValid() ? phone.number : '';
    }

    const phone = parsePhoneNumber(normalizedInput, 'CN');
    return phone?.isValid() ? phone.number : '';
  } catch {
    return '';
  }
};

const getEnterprisePayload = payload => {
  if (!payload || payload.type !== 'enterpriseSaved') return null;
  const data = parseMessageData(payload.data);
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  return data;
};

const getAllowedMessageOrigin = () => {
  if (!selectedBaseUrl.value) return '';
  try {
    return new URL(selectedBaseUrl.value).origin;
  } catch {
    return '';
  }
};

const buildContactUpdatePayload = (enterprisePayload, contactId) => {
  const name = toNormalizedText(enterprisePayload.name);
  const email = toNormalizedText(enterprisePayload.email);
  const phoneNumber = toE164PhoneNumber(enterprisePayload.mobile);
  const companyName = toNormalizedText(enterprisePayload.enterprise);

  const contactPayload = {
    id: contactId,
    customAttributes: { marketing_cloud_lead_generated: true },
  };

  if (name) contactPayload.name = name;
  if (email) contactPayload.email = email;
  if (phoneNumber) contactPayload.phoneNumber = phoneNumber;

  if (companyName) {
    contactPayload.additionalAttributes = { companyName };
  }

  return contactPayload;
};

const syncEnterprisePayloadToContact = async enterprisePayload => {
  const contactId = props.currentChat.meta?.sender?.id;
  if (!contactId) return;

  const contactPayload = buildContactUpdatePayload(
    enterprisePayload,
    contactId
  );
  if (!contactPayload) return;

  await store.dispatch('contacts/update', contactPayload);
};

const handleIframeMessage = async event => {
  const allowedOrigin = getAllowedMessageOrigin();
  if (allowedOrigin && event.origin !== allowedOrigin) {
    return;
  }

  const payload = parseMessageData(event.data);
  const enterprisePayload = getEnterprisePayload(payload);
  if (!enterprisePayload) return;

  // eslint-disable-next-line no-console
  console.log(
    '[MarketingCloudSidebar] enterpriseSaved message:',
    enterprisePayload
  );

  await syncEnterprisePayloadToContact(enterprisePayload);
};

onMounted(() => {
  window.addEventListener('message', handleIframeMessage);
});

onBeforeUnmount(() => {
  window.removeEventListener('message', handleIframeMessage);
});
</script>

<template>
  <div class="h-full w-full bg-n-background">
    <!-- 正常显示 iframe -->
    <div class="h-full w-full flex flex-col">
      <iframe
        :key="props.currentChat.id"
        :src="iframeSrc"
        class="w-full h-full border-0"
        :title="t('MARKETING_CLOUD.TITLE')"
      />
    </div>
  </div>
</template>
